import {
  claimFontFaceAddresses,
  type FontFaceClaim,
} from "@hightouch/lib/brand-profiles/assets/font-face-addresses";
import type { InjectableFont } from "src/pages/ad-studio/hooks/use-font-injection";

type CustomFont = Extract<InjectableFont, { type: "custom" }>;

/** A face the provider registers itself, with the font it came from. */
type LoadedFace =
  | {
      kind: "file";
      font: Extract<InjectableFont, { type: "file" }>;
      name: string;
      family?: string;
      weight?: string;
      style?: string;
    }
  | {
      kind: "custom-variant";
      font: CustomFont;
      variant: CustomFont["variants"][number];
      name: string;
      family: string;
      weight: string;
      style: string;
    };

function loadedFaces(font: InjectableFont): LoadedFace[] {
  switch (font.type) {
    case "file": {
      return [
        {
          kind: "file",
          font,
          name: font.name,
          family: font.family,
          weight: font.weight,
          style: font.style,
        },
      ];
    }
    case "custom": {
      return font.variants.map((variant) => ({
        kind: "custom-variant",
        font,
        variant,
        name: font.name,
        family: font.name,
        weight: variant.weight,
        style: variant.style,
      }));
    }
    // Stylesheets register these faces, so the provider cannot add them one address at a time.
    case "google":
    case "hosted-css":
    case "web-safe": {
      return [];
    }
    default: {
      throw new Error(`Unknown font: ${JSON.stringify(font satisfies never)}`);
    }
  }
}

/**
 * The fonts with each address registered by one face, so list order decides which face renders instead of load timing.
 * The rule is shared with the backend renderers (`claimFontFaceAddresses`), so the editor, the canvas and exports pick the same face.
 */
export function oneFontFacePerAddress(
  fonts: readonly InjectableFont[],
): InjectableFont[] {
  const claims = new Map<object, FontFaceClaim>(
    claimFontFaceAddresses(fonts.flatMap(loadedFaces)).map(
      ({ face, claim }) => [
        face.kind === "file" ? face.font : face.variant,
        claim,
      ],
    ),
  );
  return fonts.flatMap((font): InjectableFont[] => {
    switch (font.type) {
      case "file": {
        const claim = claims.get(font);
        if (claim === "family-and-name") {
          return [font];
        }
        if (claim === "name-only") {
          const { family: _shadowedFamily, ...aliasesOnly } = font;
          return [aliasesOnly];
        }
        return [];
      }
      case "custom": {
        const variants = font.variants.filter(
          (variant) => claims.get(variant) === "family-and-name",
        );
        return variants.length > 0 ? [{ ...font, variants }] : [];
      }
      case "google":
      case "hosted-css":
      case "web-safe": {
        return [font];
      }
      default: {
        throw new Error(
          `Unknown font: ${JSON.stringify(font satisfies never)}`,
        );
      }
    }
  });
}
